import React, { useMemo, useState, useEffect } from "react";

export default function MembersListTable({ membersProp = [], loading = false }) {
  const members = useMemo(
    () => (Array.isArray(membersProp) ? membersProp : []),
    [membersProp]
  );

  // Pagination + Search state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");

  const yesNoBadge = (value) => {
    const cls =
      value === 1
        ? "text-bg-success"
        : value === 0
        ? "text-bg-secondary"
        : "text-bg-warning";
    return <span className={`badge ${cls}`}>{value === 0 ? "No" : "Yes"}</span>;
  };

  // ✅ Format: "20 Feb, 2026"
  const formatDateCreated = (value) => {
    if (!value) return "-";

    // Supports:
    // - ISO strings: "2026-02-20T10:00:00Z"
    // - SQL datetime: "2026-02-20 10:00:00"
    // - Date object / timestamp
    const d =
      value instanceof Date
        ? value
        : new Date(
            typeof value === "string" ? value.replace(" ", "T") : value
          );

    if (Number.isNaN(d.getTime())) return "-";

    // Use UTC to avoid shifting day due to timezone differences
    const day = String(d.getUTCDate()).padStart(2, "0");
    const month = d.toLocaleString("en-GB", { month: "short", timeZone: "UTC" });
    const year = d.getUTCFullYear();

    return `${day} ${month}, ${year}`;
  };

  // Search filter (client-side)
  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;

    const safe = (v) => (v ?? "").toString().toLowerCase();

    return members.filter((m) => {
      const fullName = `${m.firstname || ""} ${m.lastname || ""}`;
      const createdText = formatDateCreated(m.date_created);

      return (
        safe(fullName).includes(q) ||
        safe(m.email).includes(q) ||
        safe(m.phone).includes(q) ||
        safe(m.city).includes(q) ||
        safe(m.country).includes(q) ||
        safe(m.role).includes(q) ||
        safe(m.industry).includes(q) ||
        safe(m.occupation).includes(q) ||
        safe(createdText).includes(q)
      );
    });
  }, [members, search]);

  // Pagination calculations based on filtered list
  const totalItems = filteredMembers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Keep user's page on refresh; only adjust if page becomes invalid
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  // When user changes the search term, go to page 1
  useEffect(() => {
    setPage(1);
  }, [search]);

  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  const pagedMembers = useMemo(() => {
    return filteredMembers.slice(startIndex, endIndex);
  }, [filteredMembers, startIndex, endIndex]);

  // Build pagination items with NO duplicates (1 and last included only once)
  const paginationItems = useMemo(() => {
    const current = page;
    const total = totalPages;

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => ({
        type: "page",
        value: i + 1,
      }));
    }

    const items = [];
    const pushPage = (p) => items.push({ type: "page", value: p });
    const pushEllipsis = () => items.push({ type: "ellipsis" });

    pushPage(1);

    const left = Math.max(2, current - 1);
    const right = Math.min(total - 1, current + 1);

    if (left > 2) pushEllipsis();
    for (let p = left; p <= right; p++) pushPage(p);
    if (right < total - 1) pushEllipsis();

    pushPage(total);

    const seen = new Set();
    return items.filter((it) => {
      if (it.type === "ellipsis") return true;
      if (seen.has(it.value)) return false;
      seen.add(it.value);
      return true;
    });
  }, [page, totalPages]);

  const renderBody = () => {
    if (loading) {
      return (
        <tr>
          <td colSpan={7} className="text-center text-white-50 py-4">
            Loading members...
          </td>
        </tr>
      );
    }

    if (members.length === 0) {
      return (
        <tr>
          <td colSpan={7} className="text-center text-white-50 py-4">
            No members yet.
          </td>
        </tr>
      );
    }

    if (filteredMembers.length === 0) {
      return (
        <tr>
          <td colSpan={7} className="text-center text-white-50 py-4">
            No results found for <b>{search}</b>
          </td>
        </tr>
      );
    }

    return pagedMembers.map((m, i) => {
      const key = m.memberid || m.id || m.email || `${startIndex}-${i}`;

      return (
        <tr key={key}>
          <td>
            <div className="fw-semibold">
              {m.firstname} {m.lastname}
            </div>
            <div>
              {m.city || ""} ({m.country || ""})
            </div>
          </td>

          <td className="small">
            <div>{m.email || ""}</div>
            <div>{m.phone || ""}</div>
          </td>

          <td className="small">
            <div>{m.role || "<no role>"}</div>
            ({m.industry}) - {m.occupation}
          </td>

          <td className="small">{formatDateCreated(m.date_created)}</td>

          <td>{yesNoBadge(m.isverified)}</td>
          <td>{yesNoBadge(m.subscriber)}</td>

          <td className="text-end">
            <div className="btn-group">
              <button className="btn btn-sm btn-outline-info" type="button">
                <i className="bi bi-pencil" />
              </button>
              <button className="btn btn-sm btn-outline-danger" type="button">
                <i className="bi bi-trash" />
              </button>
            </div>
          </td>
        </tr>
      );
    });
  };

  const showingFrom = totalItems === 0 ? 0 : startIndex + 1;
  const showingTo = Math.min(endIndex, totalItems);

  return (
    <div className="card admin-card">
      <div className="card-body">
        {/* Top bar: Search + page size + summary */}
        <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
          {/* Search */}
          <div className="input-group input-group-sm" style={{ maxWidth: 320 }}>
            <span className="input-group-text bg-dark text-white border-secondary">
              <i className="bi bi-search" />
            </span>
            <input
              className="form-control bg-dark text-white border-secondary"
              placeholder="Search members..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={loading}
            />
            {search && (
              <button
                className="btn btn-outline-secondary border-secondary"
                onClick={() => setSearch("")}
                type="button"
                disabled={loading}
                title="Clear search"
              >
                <i className="bi bi-x-lg" />
              </button>
            )}
          </div>

          {/* Rows per page + summary */}
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <span className="text-white-50 small">Rows per page</span>
              <select
                className="form-select form-select-sm bg-dark text-white border-secondary"
                style={{ width: 120 }}
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                disabled={loading}
              >
                {[5, 10, 20, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-white-50 small">
              Showing <b>{showingFrom}</b>–<b>{showingTo}</b> of <b>{totalItems}</b>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-dark table-hover align-middle">
            <thead>
              <tr>
                <th>Fullname</th>
                <th>Email and Phone</th>
                <th>Details</th>
                <th>Date Registered</th>
                <th>Verified</th>
                <th>Has Subscribed</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>

            <tbody>{renderBody()}</tbody>
          </table>
        </div>

        {/* Pagination controls */}
        {filteredMembers.length > 0 && !loading && (
          <nav
            className="d-flex justify-content-end mt-3"
            aria-label="Members pagination"
          >
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link bg-dark text-white border-secondary"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  type="button"
                >
                  Prev
                </button>
              </li>

              {paginationItems.map((it, idx) => {
                if (it.type === "ellipsis") {
                  return (
                    <li key={`e-${idx}`} className="page-item disabled">
                      <span className="page-link bg-dark text-white border-secondary">
                        …
                      </span>
                    </li>
                  );
                }

                const p = it.value;
                const active = p === page;

                return (
                  <li key={p} className={`page-item ${active ? "active" : ""}`}>
                    <button
                      className={`page-link border-secondary ${
                        active ? "bg-secondary text-white" : "bg-dark text-white"
                      }`}
                      onClick={() => setPage(p)}
                      type="button"
                    >
                      {p}
                    </button>
                  </li>
                );
              })}

              <li
                className={`page-item ${page === totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link bg-dark text-white border-secondary"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  type="button"
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </div>
  );
}
